import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Checkbox,
  message,
  Tag,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  StockOutlined,
} from "@ant-design/icons";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const { TextArea } = Input;

const BrandsTab = ({ itemId }) => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [stockModalVisible, setStockModalVisible] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [form] = Form.useForm();
  const [stockForm] = Form.useForm();

  useEffect(() => {
    if (itemId) {
      fetchBrands();
    }
  }, [itemId]);

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        `${domainSettings.urls.bot.brands}?bot_item=${itemId}`,
        null,
        navigate,
      );

      if (response.ok) {
        setBrands(response.data.results || response.data || []);
      } else {
        Utilities.showError(response, "Failed to fetch brands");
      }
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (brandId) => {
    try {
      const response = await Utilities.fetchWithAuth(
        "DELETE",
        domainSettings.urls.bot.brandDetail.replace(":id", brandId),
        null,
        navigate,
      );

      if (response.ok) {
        message.success("Brand deleted successfully");
        fetchBrands();
      } else {
        Utilities.showError(response, "Failed to delete brand");
      }
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        bot_item: itemId,
        name: values.name,
        code: values.code,
        price: values.price.toString(),
        description: values.description || null,
        is_default: values.is_default || false,
        stock_quantity: values.stock_quantity || 0,
        reorder_level: values.reorder_level || 0,
        is_available: values.is_available !== false,
        is_active: values.is_active ? 1 : 0,
      };

      const method = editingBrand ? "PUT" : "POST";
      const url = editingBrand
        ? domainSettings.urls.bot.brandDetail.replace(":id", editingBrand.id)
        : domainSettings.urls.bot.brands;

      const response = await Utilities.fetchWithAuth(
        method,
        url,
        payload,
        navigate,
      );

      if (response.ok) {
        message.success(
          editingBrand
            ? "Brand updated successfully"
            : "Brand created successfully",
        );
        setModalVisible(false);
        setEditingBrand(null);
        form.resetFields();
        fetchBrands();
      } else {
        Utilities.showError(response, "Failed to save brand");
      }
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStock = async (values) => {
    setLoading(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "POST",
        domainSettings.urls.bot.updateStock.replace(":id", editingBrand.id),
        { quantity: values.quantity },
        navigate,
      );

      if (response.ok) {
        message.success("Stock updated successfully");
        setStockModalVisible(false);
        setEditingBrand(null);
        stockForm.resetFields();
        fetchBrands();
      } else {
        Utilities.showError(response, "Failed to update stock");
      }
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (brand) => {
    setEditingBrand(brand);
    form.setFieldsValue({
      name: brand.name,
      code: brand.code,
      price: parseFloat(brand.price),
      description: brand.description,
      is_default: brand.is_default,
      stock_quantity: brand.stock_quantity,
      reorder_level: brand.reorder_level,
      is_available: brand.is_available,
      is_active: brand.is_active === 1,
    });
    setModalVisible(true);
  };

  const openStockModal = (brand) => {
    setEditingBrand(brand);
    stockForm.setFieldsValue({ quantity: brand.stock_quantity || 0 });
    setStockModalVisible(true);
  };

  const getStockStatus = (brand) => {
    const qty = brand.stock_quantity || 0;
    const reorder = brand.reorder_level || 0;

    if (qty === 0) {
      return <Tag color="red">Out of Stock</Tag>;
    } else if (qty <= reorder) {
      return <Tag color="orange">Low Stock</Tag>;
    } else {
      return <Tag color="green">In Stock</Tag>;
    }
  };

  const columns = [
    {
      title: "Brand Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Space>
          {text}
          {record.is_default && <Tag color="blue">Default</Tag>}
        </Space>
      ),
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (price) => `UGX ${parseFloat(price).toFixed(2)}`,
    },
    {
      title: "Stock",
      dataIndex: "stock_quantity",
      key: "stock_quantity",
      render: (qty) => qty || 0,
    },
    {
      title: "Reorder Level",
      dataIndex: "reorder_level",
      key: "reorder_level",
      render: (level) => level || 0,
    },
    {
      title: "Status",
      key: "stock_status",
      render: (_, record) => getStockStatus(record),
    },
    {
      title: "Available",
      dataIndex: "is_available",
      key: "is_available",
      render: (available) => (
        <Tag color={available ? "green" : "red"}>
          {available ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Update Stock">
            <Button
              type="link"
              icon={<StockOutlined />}
              onClick={() => openStockModal(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this brand?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingBrand(null);
            form.resetFields();
            form.setFieldsValue({
              is_available: true,
              is_active: true,
              stock_quantity: 0,
              reorder_level: 0,
            });
            setModalVisible(true);
          }}
        >
          Add Brand
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={brands}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 5 }}
      />

      {/* Brand Form Modal */}
      <Modal
        title={editingBrand ? "Edit Brand" : "Add Brand"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingBrand(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Brand Name"
            name="name"
            rules={[
              { required: true, message: "Please enter brand name" },
              { max: 200, message: "Name cannot exceed 200 characters" },
            ]}
          >
            <Input placeholder="e.g., Standard Brand, Premium Brand" />
          </Form.Item>

          <Form.Item
            label="Brand Code"
            name="code"
            rules={[
              { required: true, message: "Please enter brand code" },
              { max: 50, message: "Code cannot exceed 50 characters" },
            ]}
          >
            <Input
              placeholder="e.g., STD, PREM"
              style={{ textTransform: "uppercase" }}
              onChange={(e) =>
                form.setFieldValue("code", e.target.value.toUpperCase())
              }
            />
          </Form.Item>

          <Form.Item
            label="Price (UGX)"
            name="price"
            rules={[{ required: true, message: "Please enter price" }]}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              style={{ width: "100%" }}
              placeholder="0.00"
            />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <TextArea rows={2} placeholder="Optional description" />
          </Form.Item>

          <Space direction="vertical" style={{ width: "100%" }}>
            <Form.Item name="is_default" valuePropName="checked" noStyle>
              <Checkbox>Set as default brand</Checkbox>
            </Form.Item>

            <Form.Item
              label="Stock Quantity"
              name="stock_quantity"
              style={{ marginTop: 16 }}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item label="Reorder Level" name="reorder_level">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="is_available" valuePropName="checked" noStyle>
              <Checkbox>Available for purchase</Checkbox>
            </Form.Item>

            <Form.Item name="is_active" valuePropName="checked" noStyle>
              <Checkbox>Active</Checkbox>
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      {/* Stock Update Modal */}
      <Modal
        title="Update Stock"
        open={stockModalVisible}
        onCancel={() => {
          setStockModalVisible(false);
          setEditingBrand(null);
          stockForm.resetFields();
        }}
        onOk={() => stockForm.submit()}
        confirmLoading={loading}
      >
        <Form form={stockForm} layout="vertical" onFinish={handleUpdateStock}>
          <Form.Item
            label="New Stock Quantity"
            name="quantity"
            rules={[{ required: true, message: "Please enter stock quantity" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BrandsTab;
